import { applicationMetadataText as metadataText } from "../../i18n/applicationMetadataPresentation";
import { packageText as pm, usePackageManagementLanguage } from "../../i18n/packageManagementPresentation";
/**
 * AppUpdateModal
 *
 * Console-style modal for triggering and monitoring an app package update.
 * Also supports channel management for apps with persistent release channels.
 *
 * Modes:
 * - "update" — standard update flow with optional channel selector
 * - "channel" — channel-only management (no update available)
 *
 * Follows the DashboardUpdateModal / AptRunnerModal pattern:
 * - Confirmation step with version diff
 * - Dark console area with job progress
 * - Auto-scroll, copy, wrap toggle
 * - Success/failure footer with toast
 */

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { ModalCloseButton } from "../ui/ModalCloseButton";
import { Spinner } from "../ui/Spinner";
import { cn } from "../../utils/cn";
import { toast } from "../../lib/toast";
import { useAppUpdate, type AppUpdateJobStatus } from "../../hooks/useAppUpdate";
import { useConsoleScroll } from "../../hooks/useConsoleScroll";
import { ConsolePanel } from "../system/ConsolePanel";
import { CancelJobButton } from "../jobs/CancelJobButton";
import { PlexCredentialModal } from "../packages/PlexCredentialModal";
import type { PlexCredentials } from "../packages/PlexCredentialModal";
import { PlexChannelSelectorModal } from "../packages/PlexChannelSelectorModal";
import type { CompanionUpdate } from "../../hooks/useUserApps";
import type { ChannelOption } from "../../hooks/useAppChannel";

// Focus trap selector — matches Popover component's implementation
const FOCUSABLE_SELECTOR =
  "button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

export type AppUpdateModalProps = {
  open: boolean;
  onClose: () => void;
  onComplete?: () => void;
  appLabel: string;
  packageName: string;
  installedVersion: string | null;
  latestVersion: string | null;
  companionUpdate?: CompanionUpdate | null;
  primaryUpdateAvailable?: boolean;
  /** Whether this app is deprecated (superseded by another). */
  deprecated?: boolean;
  /** The ID of the replacement app when deprecated. */
  replacedBy?: string | null;
  /** The display label of the replacement app (e.g., "Seerr"). */
  replacementLabel?: string | null;
  /** Whether an update is available (controls which UI elements show). */
  updateAvailable?: boolean;
  /** Current persisted release channel (e.g., "stable", "beta"). */
  currentChannel?: string | null;
  /** Available channel options for this app. Null if channels not supported. */
  availableChannels?: ChannelOption[] | null;
  /** Callback when the user confirms a channel change. */
  onChannelChange?: (channel: string) => Promise<{ ok: boolean; message?: string }>;
  /** True while a channel switch is in progress. */
  isChannelSwitching?: boolean;
  /** Pre-selected version for reinstall. When set, the modal opens in "install version X" mode. */
  selectedVersion?: string | null;
  /** Override the action endpoint — "reinstall" for version selection, "update" for standard. */
  action?: "update" | "reinstall";
};

const getStatusBadges = (isMigration: boolean, isVersionReinstall: boolean): Record<AppUpdateJobStatus, { label: string; className: string }> => ({
  idle: { label: pm("ready"), className: "bg-muted text-muted-foreground" },
  queued: { label: pm("queued"), className: "bg-yellow-500/20 text-yellow-400" },
  running: {
    label: isMigration ? pm("migrating") : isVersionReinstall ? pm("installing") : pm("updating"),
    className: "bg-blue-500/20 text-blue-400 animate-pulse",
  },
  succeeded: { label: pm("complete"), className: "bg-emerald-500/20 text-emerald-400" },
  failed: { label: pm("failed"), className: "bg-red-500/20 text-red-400" },
  aborted: { label: pm("cancelled"), className: "bg-orange-500/20 text-orange-400" },
});

export const AppUpdateModal = ({
  open,
  onClose,
  onComplete,
  appLabel,
  packageName,
  installedVersion,
  latestVersion,
  companionUpdate,
  primaryUpdateAvailable,
  deprecated,
  replacedBy,
  replacementLabel,
  updateAvailable = true,
  currentChannel,
  availableChannels,
  onChannelChange,
  isChannelSwitching,
  selectedVersion,
  action = "update",
}: AppUpdateModalProps) => {
  usePackageManagementLanguage();
  // Determine if this is a migration (deprecated app with a known replacement)
  const isMigration = Boolean(deprecated && replacedBy);
  const targetLabel = replacementLabel ?? (replacedBy ? replacedBy.charAt(0).toUpperCase() + replacedBy.slice(1) : null);
  // Determine if this is a version reinstall (user selected a specific version from the popover)
  const isVersionReinstall = Boolean(selectedVersion);
  const {
    triggerUpdate,
    abort,
    isUpdating,
    jobId,
    jobStatus,
    jobProgress,
    jobStep,
    jobError,
    jobLogTail,
    reset,
  } = useAppUpdate();

  const [phase, setPhase] = useState<"confirm" | "running" | "done">("confirm");
  const [selectedChannel, setSelectedChannel] = useState<string | null>(null);
  // Plex credential gate — opened when the backend signals credentialsRequired (PlexPass detected)
  const [plexGateOpen, setPlexGateOpen] = useState(false);
  // Plex channel selector — opened when autodetect is uncertain (detectedChannel: "unknown")
  const [channelSelectorOpen, setChannelSelectorOpen] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;
  const titleId = useId();

  const scroll = useConsoleScroll(jobLogTail.length);

  // Derived state
  const hasChannels = availableChannels != null && availableChannels.length > 1;
  const defaultChannel = availableChannels?.find((c) => c.default)?.key ?? availableChannels?.[0]?.key ?? null;
  const effectiveSelected = selectedChannel ?? currentChannel ?? defaultChannel;
  const channelChanged = hasChannels && effectiveSelected != null && effectiveSelected !== currentChannel;
  const selectedChannelOption = availableChannels?.find((c) => c.key === effectiveSelected);
  const selectedChannelLabel = metadataText(selectedChannelOption?.presentation, selectedChannelOption?.label ?? effectiveSelected ?? "");

  // Downgrade target: the "stable" channel when one exists. This covers
  // inverted-default apps (Ombi: beta is the default) where default-based
  // detection would fire on stable -> beta (an upgrade) and miss beta -> stable.
  // Apps without a "stable" key (Plex: public/plexpass) fall back to the default.
  const downgradeTarget = availableChannels?.some((c) => c.key === "stable")
    ? "stable"
    : defaultChannel;
  const downgradeOption = availableChannels?.find((c) => c.key === downgradeTarget);
  const downgradeLabel = metadataText(downgradeOption?.presentation, downgradeOption?.label ?? "stable");

  // Downgrade warning: switching from a pre-release channel back to stable
  // (e.g., beta/nightly -> stable, plexpass -> public)
  const isDowngrade = hasChannels
    && currentChannel != null
    && downgradeTarget != null
    && currentChannel !== downgradeTarget
    && effectiveSelected === downgradeTarget;

  // Plex Pass hint: shown when Plex's non-default ("plexpass") channel is selected.
  // Scoped to Plex so other channel apps (Sonarr, SABnzbd) never show it.
  const showPlexPassHint = packageName === "plex" && effectiveSelected === "plexpass";

  // Reset when modal opens
  useEffect(() => {
    if (open) {
      reset();
      setPhase("confirm");
      setSelectedChannel(null);
      setPlexGateOpen(false);
      setChannelSelectorOpen(false);
    }
  }, [open, reset]);

  // Transition to done when job terminal (guard on phase === "running" to prevent double-fire)
  useEffect(() => {
    if (phase !== "running") return;
    if (jobStatus === "succeeded") {
      setPhase("done");
      if (isMigration && targetLabel) {
        toast.success(pm("migrationDone", { target: targetLabel ?? "" }));
      } else if (isVersionReinstall) {
        toast.success(pm("reinstallDone", { app: appLabel }));
      } else {
        toast.success(pm("updateDone", { app: appLabel }));
      }
      onCompleteRef.current?.();
    } else if (jobStatus === "failed") {
      setPhase("done");
      toast.error(
        isMigration && targetLabel
          ? pm("migrationFailed", { target: targetLabel ?? "" })
          : isVersionReinstall
            ? pm("installFailed", { app: `${appLabel} ${selectedVersion}` })
            : pm("updateAppFailed", { app: appLabel }),
        { description: jobError ?? pm("logsDetails") },
      );
    }
  }, [jobStatus, jobError, appLabel, phase, isMigration, targetLabel, isVersionReinstall, selectedVersion]);

  const handleCopyOutput = useCallback(() => {
    scroll.handleCopyOutput(jobLogTail);
  }, [jobLogTail, scroll]);

  const handleStartUpdate = useCallback(async (
    plexCredentials?: PlexCredentials,
    plexChannel?: "free" | "plexpass",
  ) => {
    // If channel changed, switch it first
    if (channelChanged && onChannelChange && effectiveSelected) {
      const result = await onChannelChange(effectiveSelected);
      if (!result.ok) {
        toast.error(pm("switchChannelFailed"), {
          description: result.message ?? pm("channelChangeFailed"),
        });
        return;
      }
    }
    setPhase("running");
    const result = await triggerUpdate(packageName, {
      ...(selectedVersion ? { version: selectedVersion, action } : { action }),
      ...(plexChannel ? { plexChannel } : {}),
      ...(plexCredentials ? { plexCredentials } : {}),
    });

    if (!result.ok) {
      // Autodetect: channel unknown — user must pick Free or PlexPass
      if ("needsChannelSelection" in result && result.needsChannelSelection) {
        setPhase("confirm");
        setChannelSelectorOpen(true);
        return;
      }
      // Autodetect: PlexPass detected — collect plex.tv credentials
      if ("credentialsRequired" in result && result.credentialsRequired) {
        setPhase("confirm");
        setPlexGateOpen(true);
      }
    }
  }, [channelChanged, onChannelChange, effectiveSelected, triggerUpdate, packageName, selectedVersion, action]);

  /** Called when the user selects Free in the channel selector. Resubmits with plexChannel: "free". */
  const handleChannelSelectorFree = useCallback(async () => {
    setChannelSelectorOpen(false);
    await handleStartUpdate(undefined, "free");
  }, [handleStartUpdate]);

  /**
   * Called when the user selects PlexPass in the channel selector.
   * Skips the resubmit roundtrip — opens PlexCredentialModal directly
   * with plexChannel: "plexpass" ready to send on credential submit.
   */
  const handleChannelSelectorPlexPass = useCallback(() => {
    setChannelSelectorOpen(false);
    setPlexGateOpen(true);
  }, []);

  /** Called when the user submits credentials in the Plex credential gate modal. */
  const handlePlexCredentialSubmit = useCallback(async (creds: PlexCredentials) => {
    setPlexGateOpen(false);
    await handleStartUpdate(creds, "plexpass");
  }, [handleStartUpdate]);

  const handleSaveChannelOnly = useCallback(async () => {
    if (!onChannelChange || !effectiveSelected) return;
    const result = await onChannelChange(effectiveSelected);
    if (result.ok) {
      toast.success(pm("channelUpdated"), {
        description: pm("channelNow", { app: appLabel, channel: metadataText(selectedChannelOption?.presentation, selectedChannelOption?.label ?? effectiveSelected ?? "") }),
      });
      onClose();
    } else {
      toast.error(pm("switchChannelFailed"), {
        description: result.message ?? pm("channelChangeFailed"),
      });
    }
  }, [onChannelChange, effectiveSelected, appLabel, selectedChannelLabel, onClose]);

  const handleClose = useCallback(() => {
    if (isUpdating) return;
    reset();
    onClose();
  }, [isUpdating, reset, onClose]);

  /** Called by CancelJobButton after a successful abort API call. */
  const handleAborted = useCallback((status: string) => {
    abort();
    if (status === "aborted") {
      setPhase("done");
    }
    onCompleteRef.current?.();
  }, [abort]);

  // Escape key handler + focus trap
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        if (!isUpdating) handleClose();
        return;
      }

      if (e.key === "Tab" && panelRef.current) {
        const focusable = Array.from(
          panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, isUpdating, handleClose]);

  // Focus the panel on open
  useEffect(() => {
    if (open && panelRef.current) {
      const timer = window.setTimeout(() => panelRef.current?.focus(), 0);
      return () => window.clearTimeout(timer);
    }
  }, [open]);

  if (!open) return null;

  const badge = getStatusBadges(isMigration, isVersionReinstall)[jobStatus];
  const isTerminal = jobStatus === "succeeded" || jobStatus === "failed" || jobStatus === "aborted";
  const showConsole = phase !== "confirm";

  const headerTitle = isMigration && targetLabel
    ? pm("migrateApp", { app: appLabel, target: targetLabel ?? "" })
    : isVersionReinstall
      ? pm("installVersion", { app: appLabel, version: selectedVersion ?? "" })
      : updateAvailable
        ? (selectedChannelLabel ? pm("updateChannel", { app: appLabel, channel: selectedChannelLabel }) : pm("updateApp", { app: appLabel }))
        : pm("channelSettings", { app: appLabel });

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-in fade-in-0 duration-200"
        onClick={!isUpdating ? handleClose : undefined}
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="relative w-full max-w-3xl mx-4 animate-in fade-in-0 zoom-in-95 duration-200 outline-none"
      >
        <div className="rounded-xl border border-white/[0.08] bg-card shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/[0.04]">
                <Icon
                  icon={
                    isMigration
                      ? "lucide:arrow-right-left"
                      : isVersionReinstall
                        ? "lucide:download"
                        : updateAvailable
                          ? "lucide:arrow-up-circle"
                          : "lucide:git-branch"
                  }
                  className="h-4 w-4 text-foreground"
                />
              </div>
              <div>
                <h2 id={titleId} className="text-sm font-semibold text-foreground">
                  {headerTitle}
                </h2>
                <p className="text-[10px] text-muted-foreground">
                  {isVersionReinstall ? pm("versionInstall") : updateAvailable ? pm("systemResponse") : pm("releaseChannel")}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {jobId && (
                <CancelJobButton
                  jobScope="package-user"
                  jobId={jobId}
                  isActive={isUpdating}
                  onAborted={handleAborted}
                />
              )}
              <span
                className={cn(
                  "inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-medium",
                  badge.className
                )}
              >
                {badge.label}
              </span>
              <ModalCloseButton onClick={handleClose} disabled={isUpdating} label={pm("closeDialog")} />
            </div>
          </div>

          {/* Confirmation view */}
          {phase === "confirm" && (
            <div className="px-5 py-6 space-y-4">
              {/* Description text */}
              {isMigration && targetLabel ? (
                <p className="text-sm text-foreground">
                  {pm("confirmMigration", { app: appLabel, target: targetLabel })}
                </p>
              ) : isVersionReinstall ? (
                <p className="text-sm text-foreground">
                  {pm("confirmVersion", { app: appLabel, version: selectedVersion ?? "" })}
                </p>
              ) : updateAvailable ? (
                <p className="text-sm text-foreground">
                  {pm("confirmUpdate", { app: appLabel })}
                </p>
              ) : (
                <p className="text-sm text-foreground">
                  {pm("manageChannel", { app: appLabel })}
                </p>
              )}

              {/* Version diff — shown for updates and version reinstalls */}
              {(isVersionReinstall || (updateAvailable && (!companionUpdate || primaryUpdateAvailable !== false))) && (
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">{pm("installed")}</span>
                    <code className="rounded bg-muted/60 px-1.5 py-[2px] font-mono text-[10px]">
                      {installedVersion ?? pm("unknown")}
                    </code>
                  </div>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground">
                      {isMigration && targetLabel
                        ? pm("latestTarget", { target: targetLabel ?? "" })
                        : isVersionReinstall
                          ? pm("selected")
                          : pm("latest")}
                    </span>
                    <code className="rounded bg-amber-500/10 px-1.5 py-[2px] font-mono text-[10px] text-amber-600 dark:text-amber-400">
                      {isVersionReinstall ? selectedVersion : (latestVersion ?? pm("unknown"))}
                    </code>
                  </div>
                </div>
              )}

              {updateAvailable && companionUpdate && !isVersionReinstall && (
                <div className="space-y-1.5 text-xs">
                  <p className="font-medium text-foreground">{companionUpdate.packageName}</p>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-muted-foreground">{pm("installed")}</span>
                    <code className="min-w-0 break-all text-right font-mono">{companionUpdate.currentVersion}</code>
                  </div>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-muted-foreground">{pm("latest")}</span>
                    <code className="min-w-0 break-all text-right font-mono">{companionUpdate.availableVersion}</code>
                  </div>
                </div>
              )}

              {/* Channel selector */}
              {hasChannels && availableChannels && (
                <ChannelSelector
                  channels={availableChannels}
                  selected={effectiveSelected}
                  onSelect={setSelectedChannel}
                  disabled={isChannelSwitching}
                />
              )}

              {/* Plex Pass channel hint — informational, preference-only.
                  The marker is written now; a signed-in Plex account (provided
                  at install/update time) is required to actually pull Plex Pass
                  builds. Without a token the update check falls back to public. */}
              {showPlexPassHint && (
                <div className="flex items-start gap-2 rounded-lg border border-border/40 bg-muted/20 px-3 py-2">
                  <Icon icon="lucide:info" aria-hidden="true" className="h-4 w-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {pm("plexChannelWarning")}
                  </p>
                </div>
              )}

              {/* Downgrade warning */}
              {isDowngrade && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                  <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-amber-600 [.theme-smoked_&]:text-amber-400 [.theme-spectre_&]:text-amber-400 [.theme-reel_&]:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 [.theme-smoked_&]:text-amber-200/90 [.theme-spectre_&]:text-amber-200/90 [.theme-reel_&]:text-amber-200/90">
                    {pm("channelDowngrade", { version: installedVersion ?? pm("unknown"), channel: downgradeLabel })}
                  </p>
                </div>
              )}

              {/* Standard warning — shown for updates and version reinstalls, not when showing downgrade warning */}
              {(updateAvailable || isVersionReinstall) && !isDowngrade && (
                <div className="flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
                  <Icon icon="lucide:alert-triangle" className="h-4 w-4 text-amber-600 [.theme-smoked_&]:text-amber-400 [.theme-spectre_&]:text-amber-400 [.theme-reel_&]:text-amber-400 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-amber-800 [.theme-smoked_&]:text-amber-200/90 [.theme-spectre_&]:text-amber-200/90 [.theme-reel_&]:text-amber-200/90">
                    {isMigration && targetLabel
                      ? pm("migrationRestart", { app: appLabel, target: targetLabel ?? "" })
                      : isVersionReinstall
                        ? pm("reinstallRestart")
                        : pm("updateRestart")}
                  </p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="ghost" size="sm" onClick={handleClose}>
                  {pm("cancel")}</Button>
                {isVersionReinstall ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void handleStartUpdate()}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Icon icon="lucide:download" className="h-3.5 w-3.5 mr-1.5" />
                    {pm("install")}{selectedVersion}
                  </Button>
                ) : updateAvailable ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void handleStartUpdate()}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    <Icon icon={isMigration ? "lucide:arrow-right-left" : "lucide:arrow-up-circle"} className="h-3.5 w-3.5 mr-1.5" />
                    {isMigration && targetLabel
                      ? pm("migrateTo", { target: targetLabel ?? "" })
                      : channelChanged
                        ? pm("switchUpdate")
                        : pm("updateApp", { app: appLabel })}
                  </Button>
                ) : channelChanged ? (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => void handleSaveChannelOnly()}
                    disabled={isChannelSwitching}
                  >
                    {isChannelSwitching ? (
                      <Spinner size="3.5" className="mr-1.5" />
                    ) : (
                      <Icon icon="lucide:save" className="h-3.5 w-3.5 mr-1.5" />
                    )}
                    {pm("saveChannel")}</Button>
                ) : null}
              </div>
            </div>
          )}

          {/* Console output view */}
          {showConsole && (
            <>
              <ConsolePanel
                lines={jobLogTail}
                consoleRef={scroll.consoleRef}
                onScroll={scroll.handleScroll}
                wrapLines={scroll.wrapLines}
                onToggleWrap={() => scroll.setWrapLines((prev) => !prev)}
                autoScroll={scroll.autoScroll}
                onScrollToBottom={scroll.scrollToBottom}
                onCopy={handleCopyOutput}
                progress={jobProgress}
                stepLabel={jobStep}
                isLoading={isUpdating}
              />

              {/* Progress bar for running state */}
              {isUpdating && jobProgress > 0 && (
                <div className="px-5 py-2 border-t border-white/[0.04]">
                  <div className="h-1 w-full rounded-full bg-white/[0.06] overflow-hidden">
                    <div
                      className="h-full rounded-full bg-blue-500 transition-all duration-500"
                      style={{ width: `${jobProgress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Running footer */}
              {isUpdating && (
                <div className="flex items-center gap-3 px-5 py-3 border-t border-white/[0.06] bg-blue-500/5">
                  <Spinner size="4" className="text-blue-400" />
                  <span className="text-xs text-blue-400">
                    {jobStep ?? (isMigration && targetLabel
                      ? pm("migratingTo", { target: targetLabel ?? "" })
                      : isVersionReinstall
                        ? pm("installingVersion", { app: appLabel, version: selectedVersion ?? "" })
                        : pm("updatingPackage"))}
                  </span>
                </div>
              )}

              {/* Success footer */}
              {jobStatus === "succeeded" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-muted-foreground">
                    {isMigration && targetLabel
                      ? pm("migrationDoneSentence", { target: targetLabel ?? "" })
                      : isVersionReinstall
                        ? pm("reinstallComplete")
                        : pm("updateComplete")}
                  </div>
                  <Button variant="secondary" size="sm" onClick={handleClose}>
                    {pm("close")}</Button>
                </div>
              )}

              {/* Failed footer */}
              {jobStatus === "failed" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-red-400/80 flex-1 min-w-0 truncate">
                    {jobError ?? (isMigration && targetLabel
                      ? pm("migrationFailed", { target: targetLabel ?? "" })
                      : isVersionReinstall
                        ? pm("versionFailed", { version: selectedVersion ?? "" })
                        : pm("updateFailed"))}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="ghost" size="sm" onClick={handleCopyOutput}>
                      {pm("copyLogs")}</Button>
                    <Button variant="secondary" size="sm" onClick={handleClose}>
                      {pm("close")}</Button>
                  </div>
                </div>
              )}

              {/* Aborted footer */}
              {jobStatus === "aborted" && (
                <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-white/[0.06]">
                  <div className="text-xs text-orange-400/80">{pm("jobCancelled")}</div>
                  <Button variant="secondary" size="sm" onClick={handleClose}>
                    {pm("close")}</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );

  return (
    <>
      {createPortal(modal, document.body)}
      {/* Plex channel selector — opened when autodetect is uncertain */}
      <PlexChannelSelectorModal
        open={channelSelectorOpen}
        onSelectFree={() => { void handleChannelSelectorFree(); }}
        onSelectPlexPass={handleChannelSelectorPlexPass}
        onClose={() => {
          setChannelSelectorOpen(false);
          handleClose();
        }}
      />
      {/* Plex credential gate — opened when PlexPass is detected or selected */}
      <PlexCredentialModal
        open={plexGateOpen}
        onSubmit={handlePlexCredentialSubmit}
        onClose={() => {
          setPlexGateOpen(false);
          // User dismissed the credential modal — treat as a cancel, close the update modal too
          handleClose();
        }}
      />
    </>
  );
};

// ── Channel Selector ─────────────────────────────────────────────────────────

/**
 * Segmented control for switching between release channels.
 * Matches the PackageInstallDialog version selector pattern.
 */
const ChannelSelector = ({
  channels,
  selected,
  onSelect,
  disabled,
}: {
  channels: ChannelOption[];
  selected: string | null;
  onSelect: (key: string) => void;
  disabled?: boolean;
}) => (
  <div className="space-y-1.5 pt-1">
    <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      {pm("releaseChannel")}</label>
    <div
      className={cn(
        "inline-flex w-fit rounded-md border border-border/50 overflow-hidden",
        disabled && "opacity-50 pointer-events-none",
      )}
      role="radiogroup"
      aria-label={pm("releaseChannel")}
    >
      {channels.map((ch) => (
        <button
          key={ch.key}
          type="button"
          role="radio"
          aria-checked={selected === ch.key}
          onClick={() => onSelect(ch.key)}
          disabled={disabled}
          className={cn(
            "px-3.5 py-1.5 text-xs font-medium transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-1 focus-visible:ring-offset-background",
            "focus-visible:z-10 relative",
            selected === ch.key
              ? "bg-primary/15 text-primary-text"
              : "bg-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40",
          )}
        >
          {metadataText(ch.presentation, ch.label)}
          {ch.default && (
            <span className="ml-1.5 text-[10px] text-muted-foreground">{pm("default")}</span>
          )}
        </button>
      ))}
    </div>
  </div>
);
